import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { getMomentTz } from "@dexteel/mesf-core";
import type { GridApi } from "ag-grid-community";
import { useEffect, useState, type ReactNode } from "react";
import { useConsumptionReportPeriod } from "../hooks/useConsumptionReportPeriod";
import { useUtilityConsumptionReport } from "../hooks/useUtilityConsumptionReport";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	UTILITY_CONSUMPTION_REPORT_ROW_KIND,
	UTILITY_CONSUMPTION_UTILITY,
	type UtilityConsumptionMeter,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
	type UtilityConsumptionUtility,
} from "../models/UtilityConsumption";
import {
	getUtilityConsumptionReportBucketLabels,
	isUtilityConsumptionNextDisabled,
} from "../utils/calendar";
import {
	createConsumptionReportGridRow,
	type ConsumptionReportGridRow,
} from "../utils/reportGrid";
import { ConsumptionFilterBar } from "./ConsumptionFilterBar";
import { ConsumptionReportControls } from "./ConsumptionReportControls";
import { ConsumptionReportGrid } from "./ConsumptionReportGrid";
import {
	ConsumptionError,
	ConsumptionMessage,
	WipState,
} from "./ConsumptionStates";

const moment = getMomentTz();

const rowHeaders: Partial<Record<UtilityConsumptionReportMode, string>> = {
	day: "Hour",
	year: "Month",
};
const totalHeaders: Partial<Record<UtilityConsumptionReportMode, string>> = {
	day: "Hourly Total",
	year: "Monthly Total",
	section: "Event Total",
};

interface ConsumptionReportProps {
	targetId: number | null;
	utility: UtilityConsumptionUtility;
	meters: UtilityConsumptionMeter[];
	isMeterLoading: boolean;
	selectedMeterIds: string[] | null;
	resolvedMeterIds: string[] | null;
	areMetersLoaded: boolean;
	isEmptySelection: boolean;
	range: UtilityConsumptionReportRange;
	plantTimeZone: string;
	areaSelector?: ReactNode;
	showCost: boolean;
	showRatio?: boolean;
	showSection?: boolean;
	onMeterChange: (meterIds: string[] | null) => void;
	onRangeChange: (range: UtilityConsumptionReportRange) => void;
}

export const ConsumptionReport = ({
	targetId,
	utility,
	meters,
	isMeterLoading,
	selectedMeterIds,
	resolvedMeterIds,
	areMetersLoaded,
	isEmptySelection,
	range,
	plantTimeZone,
	areaSelector,
	showCost,
	showRatio,
	showSection,
	onMeterChange,
	onRangeChange,
}: ConsumptionReportProps) => {
	const [gridApi, setGridApi] =
		useState<GridApi<ConsumptionReportGridRow> | null>(null);
	const [sectionId, setSectionId] = useState<number | null>(null);
	const reportPeriod = useConsumptionReportPeriod({
		plantTimeZone,
		range,
		onRangeChange,
	});
	const { mode, isSection } = reportPeriod;

	const millNames = [...new Set(meters.map((meter) => meter.MillName))];
	const isWorkInProgress = utility !== UTILITY_CONSUMPTION_UTILITY.WATER;

	const {
		data,
		period,
		isError,
		error,
		isLoading,
		dateError,
		isFetching,
		refetch,
	} = useUtilityConsumptionReport({
		targetId,
		utility,
		externalMeterIds: resolvedMeterIds,
		areMetersLoaded: areMetersLoaded && !isEmptySelection,
		mode,
		sectionId,
		referenceDate: reportPeriod.referenceDate,
		sectionEndBoundary: reportPeriod.sectionEndBoundary,
		range,
		sectionRange: reportPeriod.sectionRange,
		plantTimeZone,
	});

	const reportRows = data?.Rows ?? [];
	const reportMeterIds = data?.MeterIds ?? [];

	const shownMillNames = new Set(
		meters
			.filter((meter) => reportMeterIds.includes(meter.ExternalMeterId))
			.map((meter) => meter.MillName),
	);
	const periodLabels = isSection
		? []
		: getUtilityConsumptionReportBucketLabels(
				reportRows.map((row) => row.PeriodStartUtc),
				mode,
				plantTimeZone,
			);
	const formatEventTime = (value: Date) =>
		moment.utc(value).tz(plantTimeZone).format("MM/DD/YYYY HH:mm");

	const rows: ConsumptionReportGridRow[] = reportRows.map((row, index) => {
		const values = {
			gallonsPerTon: row.GallonsPerTon,
			gallonsPerBillet: row.GallonsPerBillet,
			meterIds: reportMeterIds,
			meterGallons: row.MeterGallons,
			meterCoverage: row.MeterCoverage,
			gallons: row.PeriodGallons,
			costPerTon: row.CostPerTon,
			cost: row.PeriodCost,
			isCostApproximate: row.IsCostApproximate,
		};

		return row.Kind === UTILITY_CONSUMPTION_REPORT_ROW_KIND.CALENDAR
			? createConsumptionReportGridRow({
					...values,
					id: row.PeriodStartUtc.toISOString(),
					period: periodLabels[index],
				})
			: createConsumptionReportGridRow({
					...values,
					id: `event:${row.MillAssetId}:${row.EventId}`,
					mill: row.MillName,
					section: row.SectionName,
					event: `#${row.EventId}`,
					eventStart: formatEventTime(row.EventStartUtc),
					eventEnd: row.EventEndUtc ? formatEventTime(row.EventEndUtc) : "Open",
				});
	});

	const exportDisabled =
		isFetching ||
		isError ||
		dateError !== null ||
		isWorkInProgress ||
		rows.length === 0 ||
		gridApi === null;

	const getExportRangeKey = () => {
		const compact = (dateKey: string) => dateKey.replaceAll("-", "");
		const local = (value: Date) => moment.utc(value).tz(plantTimeZone);
		const { sectionRange } = reportPeriod;

		if (isSection) {
			return `section_${compact(sectionRange.startDateKey)}_${compact(sectionRange.endDateKey)}`;
		}
		if (mode === UTILITY_CONSUMPTION_REPORT_MODE.PERIOD) {
			return `${compact(range.startDateKey)}_${compact(range.endDateKey)}`;
		}
		if (period === null) return "";
		if (mode === UTILITY_CONSUMPTION_REPORT_MODE.YEAR) {
			return local(period.startUtc).format("YYYY");
		}
		return `${local(period.startUtc).format("YYYYMMDD")}_${local(period.endUtc).subtract(1, "day").format("YYYYMMDD")}`;
	};
	const exportToExcel = () => {
		if (exportDisabled || gridApi === null) return;
		const hasPartialCells = (data?.Rows ?? []).some((row) =>
			Object.values(row.MeterCoverage).some(
				(coverage) => coverage !== null && coverage < 100,
			),
		);
		gridApi.exportDataAsExcel({
			fileName: `utility_report_water_${getExportRangeKey()}.xlsx`,
			sheetName: "Utility Report",
			skipColumnGroupHeaders: false,
			skipPinnedBottom: false,
			appendContent: hasPartialCells
				? [
						{ cells: [] },
						{
							cells: [
								{
									data: {
										value:
											"Shaded: the meter reported only part of that period, so the figure is what it measured, not what went through it.",
										type: "String",
									},
									styleId: "partialCoverage",
									mergeAcross: 4,
								},
							],
						},
					]
				: undefined,
		});
	};
	useEffect(() => {
		setSectionId(null);
	}, [targetId]);

	return (
		<>
			<ConsumptionFilterBar
				meters={meters}
				isMeterLoading={isMeterLoading}
				selectedMeterIds={selectedMeterIds}
				areaSelector={areaSelector}
				onMeterChange={onMeterChange}
				actions={
					<ExcelButton
						handleExportToExcel={exportToExcel}
						disabled={exportDisabled}
					/>
				}
			>
				<ConsumptionReportControls
					period={reportPeriod}
					showSection={showSection}
					sectionId={sectionId}
					millNames={millNames}
					nextDisabled={
						period === null ||
						isUtilityConsumptionNextDisabled(period, plantTimeZone)
					}
					dateError={dateError}
					onSectionChange={setSectionId}
				/>
			</ConsumptionFilterBar>
			{isWorkInProgress ? (
				<WipState utility={utility} />
			) : isEmptySelection ? (
				<ConsumptionMessage
					title="No meters selected"
					detail="Tick at least one meter in the filter to see the report."
				/>
			) : (
				<>
					{isError && (
						<ConsumptionError
							message={
								error instanceof Error ? error.message : "Unexpected response."
							}
							onRetry={() => refetch()}
						/>
					)}
					{!isLoading &&
						!isError &&
						dateError === null &&
						areMetersLoaded &&
						rows.length === 0 && <ConsumptionMessage title="No report rows" />}
					<ConsumptionReportGrid
						rows={rows}
						meterIds={reportMeterIds}
						meters={meters}
						meterTotalGallons={data?.MeterTotalGallons ?? {}}
						grandTotalGallons={data?.GrandTotalGallons ?? null}
						grandGallonsPerTon={data?.GrandGallonsPerTon ?? null}
						grandGallonsPerBillet={data?.GrandGallonsPerBillet ?? null}
						grandTotalCost={data?.GrandTotalCost ?? null}
						grandCostPerTon={data?.GrandCostPerTon ?? null}
						isCostApproximate={data?.IsCostApproximate ?? false}
						showCost={showCost}
						showRatio={showRatio}
						isSection={isSection}
						showMill={shownMillNames.size > 1}
						periodHeader={rowHeaders[mode] ?? "Date"}
						totalHeader={totalHeaders[mode] ?? "Daily Total"}
						isLoading={isFetching}
						onGridReady={setGridApi}
					/>
				</>
			)}
		</>
	);
};
